import React from "react";
import { AiAmrEnergyConsumption, AiAmrTrainingZone } from "./aiAmrSummaryTypes";
import classes from "./AiAmrZonePanels.module.css";

type Props = {
  trainingZones: AiAmrTrainingZone[];
  energyConsumption: AiAmrEnergyConsumption[];
  variant?: "screen" | "pdf";
};

const zoneToneClasses: Record<number, string> = {
  1: classes.zoneOne,
  2: classes.zoneTwo,
  3: classes.zoneThree,
  4: classes.zoneFour,
  5: classes.zoneFive,
};

const formatNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 1,
  }).format(value);

const formatRange = (
  lower: number | undefined,
  upper: number | undefined,
  unit: string
): string => {
  if (lower !== undefined && upper !== undefined) {
    return `${formatNumber(lower)}\u2013${formatNumber(upper)} ${unit}`;
  }
  if (lower !== undefined) {
    return `From ${formatNumber(lower)} ${unit}`;
  }
  if (upper !== undefined) {
    return `Up to ${formatNumber(upper)} ${unit}`;
  }
  return "";
};

const getZoneTone = (zone: number): string =>
  zoneToneClasses[zone] ?? classes.zoneDefault;

const TrainingZones = ({ zones }: { zones: AiAmrTrainingZone[] }) => {
  const maximumHeartRate = Math.max(
    ...zones.map((zone) => zone.heartRateUpperBpm ?? 0),
    1
  );

  return (
    <section className={classes.panel} data-pdf-break="after">
      <div className={classes.panelHeader}>
        <div>
          <p>Personalized intensity</p>
          <h3>Training zones</h3>
        </div>
        <span>Heart rate</span>
      </div>
      <div className={classes.zoneRows}>
        {zones.map((zone) => {
          const heartRateLabel = formatRange(
            zone.heartRateLowerBpm,
            zone.heartRateUpperBpm,
            "bpm"
          );
          const wattsLabel = formatRange(zone.wattsLower, zone.wattsUpper, "W");
          const barWidth = Math.max(
            24,
            ((zone.heartRateUpperBpm ?? zone.heartRateLowerBpm ?? 0) /
              maximumHeartRate) *
              100
          );

          return (
            <article
              key={`training-zone-${zone.zone}`}
              className={`${classes.zoneRow} ${getZoneTone(zone.zone)}`}
            >
              <strong>Z{zone.zone}</strong>
              <div className={classes.zoneDetail}>
                {heartRateLabel && <span>HR {heartRateLabel}</span>}
                {wattsLabel && <small>{wattsLabel}</small>}
                <div className={classes.zoneBar} aria-hidden="true">
                  <i style={{ width: `${Math.min(100, barWidth)}%` }} />
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

const EnergyConsumption = ({ zones }: { zones: AiAmrEnergyConsumption[] }) => (
  <section className={classes.panel} data-pdf-break="after">
    <div className={classes.panelHeader}>
      <div>
        <p>Energy consumption</p>
        <h3>Fuel use by zone</h3>
      </div>
      <span>kcal/min</span>
    </div>
    <div className={classes.energyRows}>
      {zones.map((zone) => {
        const fat = zone.fatPercent ?? 0;
        const carbs = zone.carbPercent ?? 0;
        const total = fat + carbs || 1;

        return (
          <article
            key={`energy-zone-${zone.zone}`}
            className={`${classes.energyRow} ${getZoneTone(zone.zone)}`}
          >
            <strong>Z{zone.zone}</strong>
            <div className={classes.fuelDetail}>
              <div className={classes.fuelBar} aria-hidden="true">
                <i style={{ width: `${(fat / total) * 100}%` }} />
                <b style={{ width: `${(carbs / total) * 100}%` }} />
              </div>
              <div className={classes.fuelLabels}>
                {zone.fatPercent !== undefined && (
                  <span>Fat {formatNumber(zone.fatPercent)}%</span>
                )}
                {zone.carbPercent !== undefined && (
                  <span>Carbs {formatNumber(zone.carbPercent)}%</span>
                )}
              </div>
            </div>
            {zone.kcalPerMinute !== undefined && (
              <span className={classes.kcalValue}>
                {formatNumber(zone.kcalPerMinute)}
              </span>
            )}
          </article>
        );
      })}
    </div>
    <div className={classes.fuelLegend}>
      <span>
        <i /> Fat
      </span>
      <span>
        <b /> Carbohydrates
      </span>
    </div>
  </section>
);

const AiAmrZonePanels = ({
  trainingZones,
  energyConsumption,
  variant = "screen",
}: Props) => {
  if (trainingZones.length === 0 && energyConsumption.length === 0) {
    return null;
  }

  return (
    <div className={`${classes.root} ${variant === "pdf" ? classes.pdf : ""}`}>
      {trainingZones.length > 0 && <TrainingZones zones={trainingZones} />}
      {energyConsumption.length > 0 && (
        <EnergyConsumption zones={energyConsumption} />
      )}
    </div>
  );
};

export default AiAmrZonePanels;
